export type Operator =
  | "eq"
  | "neq"
  | "contains"
  | "notContains"
  | "startsWith"
  | "endsWith"
  | "between";

export function compareValues(
  a: unknown,
  b: unknown,
  operator: Operator,
  { caseSensitive }: { caseSensitive?: boolean } = {
    caseSensitive: false,
  },
) {
  if (operator === "eq") {
    // First try with simple equality
    if (caseSensitive ? a == b : asLowerCase(a) == asLowerCase(b)) {
      return true;
    }

    // If b is an array, check if a is in the array
    if (b instanceof Array) {
      return b.some(
        (item) =>
          (caseSensitive ? asLowerCase(item) : item) ==
          (caseSensitive ? asLowerCase(a) : a),
      );
    }
  } else if (operator === "neq") {
    return !compareValues(a, b, "eq", { caseSensitive });
  } else if (operator === "contains") {
    const aAsString = caseSensitive ? asString(a) : asString(asLowerCase(a));
    const bAsString = caseSensitive ? asString(b) : asString(asLowerCase(b));

    if (aAsString !== undefined && bAsString !== undefined) {
      return aAsString.includes(bAsString);
    }

    if (a instanceof Array && b instanceof Array) {
      return a.some((item) => b.includes(item));
    }
  } else if (operator === "notContains") {
    return !compareValues(a, b, "contains", { caseSensitive });
  } else if (operator === "startsWith") {
    const aAsString = caseSensitive ? asString(a) : asString(asLowerCase(a));
    const bAsString = caseSensitive ? asString(b) : asString(asLowerCase(b));

    if (aAsString !== undefined && bAsString !== undefined) {
      return aAsString.startsWith(bAsString);
    }
  } else if (operator === "endsWith") {
    const aAsString = caseSensitive ? asString(a) : asString(asLowerCase(a));
    const bAsString = caseSensitive ? asString(b) : asString(asLowerCase(b));

    if (aAsString !== undefined && bAsString !== undefined) {
      return aAsString.endsWith(bAsString);
    }
  } else if (operator === "between") {
    const aAsString = caseSensitive ? asString(a) : asString(asLowerCase(a));
    const bAsString = caseSensitive ? asString(b) : asString(asLowerCase(b));

    if (aAsString !== undefined && bAsString !== undefined) {
      return aAsString >= bAsString && aAsString <= bAsString;
    }

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() >= b.getTime() && a.getTime() <= b.getTime();
    }
  }
  return false;
}

function asLowerCase(value: unknown): unknown {
  if (typeof value === "string") {
    return value.toLowerCase();
  }
  return value;
}

function asString(value: unknown): string | undefined {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value.toString();
  } else if (
    typeof value === "object" &&
    value !== null &&
    value !== undefined &&
    "toString" in value &&
    typeof value.toString === "function"
  ) {
    const stringValue = (value as { toString: () => string }).toString();
    if (typeof stringValue === "string") {
      return stringValue;
    }
  }
}
